const IZOLIFE_REGIONS = ["СПБ", "МСК", "КРД"];
const IZOLIFE_PERMISSION_KEYS = {
  СПБ: "warehouses.izolife_spb",
  МСК: "warehouses.izolife_msk",
  КРД: "warehouses.izolife_krd",
};
const IZOLIFE_ALL_REGIONS_PERMISSION_KEY = "warehouses.izolife_all_regions";

const fullAccessPermission = {
  canView: true,
  canEdit: true,
  canCreate: true,
  canDelete: true,
};

const emptyPermission = {
  canView: false,
  canEdit: false,
  canCreate: false,
  canDelete: false,
};

const getExplicitPermission = (permissions, permissionKey) => {
  if (!permissions || !permissionKey) return null;
  return permissions[permissionKey] || null;
};

const hasEnabledPermission = (permission) =>
  Boolean(
    permission &&
    (permission.canView ||
      permission.canEdit ||
      permission.canCreate ||
      permission.canDelete),
  );

const hasPermissionView = (permissions, permissionKey) =>
  Boolean(getExplicitPermission(permissions, permissionKey)?.canView);

export const hasExplicitPermissions = (permissions) =>
  Boolean(
    permissions &&
    Object.values(permissions).some((permission) =>
      hasEnabledPermission(permission),
    ),
  );

export const getIzolifePermissionKey = (region) =>
  IZOLIFE_PERMISSION_KEYS[region] || null;
export const getIzolifeAllRegionsPermissionKey = () =>
  IZOLIFE_ALL_REGIONS_PERMISSION_KEY;

export const getAllowedIzolifeRegions = ({ role, permissions }) => {
  if (role === "god") {
    return [...IZOLIFE_REGIONS];
  }

  if (hasPermissionView(permissions, IZOLIFE_ALL_REGIONS_PERMISSION_KEY)) {
    return [...IZOLIFE_REGIONS];
  }

  return IZOLIFE_REGIONS.filter((region) =>
    hasPermissionView(permissions, getIzolifePermissionKey(region)),
  );
};

export const resolveIzolifePermission = ({ role, permissions, region }) => {
  const globalPermissionKey = getIzolifeAllRegionsPermissionKey();
  const regionPermissionKey = getIzolifePermissionKey(region);
  const globalPermission = getExplicitPermission(
    permissions,
    globalPermissionKey,
  );

  if (hasEnabledPermission(globalPermission)) {
    return {
      canView: Boolean(globalPermission.canView),
      canEdit: Boolean(globalPermission.canView && globalPermission.canEdit),
      canCreate: Boolean(
        globalPermission.canView && globalPermission.canCreate,
      ),
      canDelete: Boolean(
        globalPermission.canView && globalPermission.canDelete,
      ),
    };
  }

  return resolvePermission({
    role,
    permissions,
    permissionKey: regionPermissionKey,
  });
};

export const resolvePermission = ({ role, permissions, permissionKey }) => {
  if (role === "god") {
    return fullAccessPermission;
  }

  const explicit = getExplicitPermission(permissions, permissionKey);

  if (hasEnabledPermission(explicit)) {
    return {
      canView: Boolean(explicit.canView),
      canEdit: Boolean(explicit.canView && explicit.canEdit),
      canCreate: Boolean(explicit.canView && explicit.canCreate),
      canDelete: Boolean(explicit.canView && explicit.canDelete),
    };
  }

  return emptyPermission;
};

export const getPathPermissionKey = (pathname) => {
  if (pathname === "/") return "section.main";
  if (pathname.startsWith("/reference-info/"))
    return `reference-info.${pathname.split("/")[2] || "guide"}`;
  if (pathname.startsWith("/warehouses/"))
    return `warehouses.${pathname.split("/")[2] || "warehouse_stock"}`;
  if (pathname.startsWith("/delivery/"))
    return `delivery.${pathname.split("/")[2] || "delivery_data"}`;
  if (pathname.startsWith("/analog-settings/"))
    return `analog-settings.${pathname.split("/")[2] || "compression_ranges"}`;
  if (pathname === "/price_change_logs") return "page.price_change_logs";
  if (pathname === "/parser") return "page.parser";
  if (pathname === "/warnings") return "page.warnings";
  if (pathname === "/all-sources") return "page.all-sources";
  if (pathname === "/auto-updates") return "page.auto-updates";
  if (pathname === "/product-matching") return "page.product-matching";
  if (pathname === "/price-list-recognition") return "page.price-list-recognition";
  if (pathname === "/rights") return "page.rights";
  if (pathname === "/audit-log") return "page.audit-log";
  if (pathname === "/logisti-answers") return "page.logisti-answers";
  if (pathname === "/role-constructor") return "page.role-constructor";
  if (pathname === "/db-management") return "page.db-management";
  return null;
};
